import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TablePagination from '@mui/material/TablePagination'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import CloudOffIcon from '@mui/icons-material/CloudOff'
import HourglassTopIcon from '@mui/icons-material/HourglassTop'
import PersonOffIcon from '@mui/icons-material/PersonOff'
import SearchOffIcon from '@mui/icons-material/SearchOff'

import RosterRow from './RosterRow'
import { NO_MOTION, fill } from '../theme/animations'
import { MONO } from '../theme/appTheme'

const PULSE_SECONDS = 2
const TABLE_MIN_WIDTH = 760

const ROWS_PER_PAGE = [
  { value: 10, label: '10 rows' },
  { value: 25, label: '25 rows' },
  { value: 50, label: '50 rows' },
]

// One width per column, in the same order as the header cells below:
// No, Account Id, Tally Company, Tally Client, Tally, Since.
// Fixed columns: 36 + 130 + 100 + 160 = 426px; the two text columns split the rest 1.5 : 1.2.
const COL_WIDTHS = [
  '36px',
  'calc((100% - 426px) * 0.5556)',
  'calc((100% - 426px) * 0.4444)',
  '130px',
  '100px',
  '160px',
]

const headCellSx = {
  height: 34,
  // Headers stay on one line; the column widths above leave room for the longest.
  whiteSpace: 'nowrap',
  fontSize: '0.64rem',
  fontWeight: 600,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  color: 'custom.ink3',
  borderBottom: '1px solid',
  borderColor: 'custom.line',
}

/**
 * Why the table is empty — there are four different reasons and they call for
 * four different actions from whoever is reading the screen.
 *
 * Saying the wrong one is worse than saying nothing: "no users match this
 * filter" in front of someone whose server is down sends them hunting through
 * filters for a problem that is not there, and an empty table with no
 * explanation at all reads as "everyone is offline" — the one thing this page
 * exists to report, stated by accident.
 */
function emptyState(status, hasData) {
  // Checked first, and deliberately without consulting `status`: if we hold rows
  // then the filter is what emptied the view, whatever the socket is doing. The
  // connection state is already being reported by the live bar and the header,
  // so repeating it here would only bury the reason the table looks empty.
  if (hasData) {
    return { Icon: SearchOffIcon, text: 'No users match this filter.' }
  }

  if (status === 'offline') {
    return { Icon: CloudOffIcon, text: 'Not connected to a server.' }
  }

  if (status === 'connecting') {
    return { Icon: HourglassTopIcon, text: 'Connecting to the server…' }
  }

  if (status === 'reconnecting') {
    return { Icon: CloudOffIcon, text: 'Connection lost. Reconnecting…' }
  }

  // Connected, and the server's answer was a genuinely empty roster.
  return { Icon: PersonOffIcon, text: 'No clients registered yet.' }
}

/**
 * The table card: the live bar, the rows, the empty state and the pager.
 *
 * It is given one page of rows that are already filtered and sliced. It does no
 * filtering of its own — its whole job is to draw what it is handed.
 */
export default function RosterTable({
  rows,
  status,
  hasData,
  now,
  startIndex,
  total,
  page,
  perPage,
  onPageChange,
  onPerPageChange,
}) {
  const isLive = status === 'live'
  const empty = total === 0 ? emptyState(status, hasData) : null
  const EmptyIcon = empty?.Icon

  return (
    <Box
      sx={{
        bgcolor: 'custom.surface',
        border: '1px solid',
        borderColor: 'custom.line',
        borderRadius: '10px',
        overflow: 'hidden',
      }}
    >
      <TableContainer sx={{ overflowX: 'auto' }}>
        <Box sx={{ minWidth: TABLE_MIN_WIDTH }}>
          {/* Sweeps only while the socket is open, so a still bar means a dead feed. */}
          <Box sx={{ height: 2, bgcolor: 'custom.track', overflow: 'hidden' }}>
            <Box
              sx={{
                height: 2,
                bgcolor: isLive ? 'custom.up' : 'custom.downText',
                opacity: 0.5,
                transformOrigin: 'left',
                animation: isLive ? `${fill} ${PULSE_SECONDS}s linear infinite` : 'none',
                [NO_MOTION]: { animation: 'none', transform: 'scaleX(1)' },
              }}
            />
          </Box>

          <Table sx={{ tableLayout: 'fixed', minWidth: TABLE_MIN_WIDTH }}>
            <colgroup>
              {COL_WIDTHS.map((width, index) => (
                <col key={index} style={{ width }} />
              ))}
            </colgroup>

            <TableHead>
              <TableRow>
                <TableCell sx={{ ...headCellSx, pl: '12px', pr: 0 }}>No</TableCell>
                <TableCell sx={headCellSx}>Account Id</TableCell>
                <TableCell sx={headCellSx}>Tally Company</TableCell>
                <TableCell sx={headCellSx}>Tally Client</TableCell>
                <TableCell sx={headCellSx}>Tally</TableCell>
                <TableCell sx={{ ...headCellSx, textAlign: 'right' }}>Tally Since</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {rows.map((user, index) => (
                <RosterRow key={user.id} user={user} number={startIndex + index + 1} now={now} />
              ))}
            </TableBody>
          </Table>

          {empty && (
            <Stack
              sx={{
                height: 150,
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                color: 'custom.ink3',
              }}
            >
              <EmptyIcon sx={{ fontSize: 24 }} />
              <Typography sx={{ fontSize: '0.82rem' }}>{empty.text}</Typography>
            </Stack>
          )}

          {/* --------------------------------------------------- table foot */}
          <Stack
            direction="row"
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
              height: 38,
              pl: '16px',
              pr: '12px',
              borderTop: '1px solid',
              borderColor: 'custom.line',
              bgcolor: 'custom.bar',
            }}
          >
            <Typography sx={{ fontFamily: MONO, fontSize: '0.66rem', color: 'custom.ink3' }}>
              read only
            </Typography>

            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_event, next) => onPageChange(next)}
              rowsPerPage={perPage}
              onRowsPerPageChange={(event) => onPerPageChange(Number(event.target.value))}
              rowsPerPageOptions={ROWS_PER_PAGE}
              labelRowsPerPage=""
              slotProps={{
                select: {
                  renderValue: (value) => `${value} rows`,
                  'aria-label': 'Rows per page',
                  sx: {
                    height: 23,
                    borderRadius: '5px',
                    border: '1px solid',
                    borderColor: 'custom.fieldLine',
                    bgcolor: 'custom.field',
                    color: 'custom.ink2',
                    fontSize: '0.72rem',
                    m: 0,
                    '& .MuiSelect-select': { py: 0, pl: '8px', pr: '22px !important' },
                    '& fieldset': { border: 0 },
                    '& .MuiSelect-icon': { fontSize: 16, color: 'custom.ink3' },
                  },
                },
                displayedRows: {
                  sx: { fontFamily: MONO, fontSize: '0.7rem', color: 'custom.ink2' },
                },
                actions: {
                  previousButton: { 'aria-label': 'Previous page' },
                  nextButton: { 'aria-label': 'Next page' },
                },
              }}
              slots={{
                actions: {
                  previousButtonIcon: ChevronLeftIcon,
                  nextButtonIcon: ChevronRightIcon,
                },
              }}
              sx={{
                border: 0,
                overflow: 'visible',
                '& .MuiToolbar-root': {
                  minHeight: 0,
                  height: 38,
                  p: 0,
                  gap: '12px',
                },
                '& .MuiTablePagination-spacer': { display: 'none' },
                '& .MuiTablePagination-selectLabel': { display: 'none' },
                '& .MuiTablePagination-actions': {
                  ml: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1px',
                  '& .MuiIconButton-root': {
                    width: 24,
                    height: 24,
                    p: 0,
                    borderRadius: '5px',
                    color: 'custom.ink2',
                    '&:hover': { bgcolor: 'custom.chip' },
                    '&.Mui-disabled': { color: 'custom.inkDisabled' },
                  },
                  '& .MuiSvgIcon-root': { fontSize: 17 },
                },
              }}
            />
          </Stack>
        </Box>
      </TableContainer>
    </Box>
  )
}
